/**
 * Collection page boot: sort + page-size bind immediately (not idle).
 * Taxonomy stays deferred to keep TBT low.
 */
function whenIdle(callback) {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, { timeout: 2500 });
  } else {
    setTimeout(callback, 150);
  }
}

/**
 * Init page-size and sort independently so one failed import cannot
 * block show_all / loadAll (Firefox importmap / CDN edge cases).
 * @param {{
 *   importPageSize?: () => Promise<{ initCollectionPageSize: Function }>,
 *   importSort?: () => Promise<{ initCollectionSort: Function }>,
 *   hasPageSize?: boolean,
 *   hasSort?: boolean,
 * }} [options]
 */
export async function bootCollectionControls(options = {}) {
  const importPageSize = options.importPageSize ?? (() => import('#collection-page-size'));
  const importSort = options.importSort ?? (() => import('#collection-sort'));
  const hasPageSize =
    options.hasPageSize ?? Boolean(document.querySelector('[data-collection-page-size]'));
  const hasSort = options.hasSort ?? Boolean(document.querySelector('[data-collection-sort]'));

  const pageSizeTask = (async () => {
    if (!hasPageSize) return;
    const pageSizeMod = await importPageSize();
    pageSizeMod.initCollectionPageSize();
  })();

  const sortTask = (async () => {
    if (!hasSort) return;
    const sortMod = await importSort();
    sortMod.initCollectionSort();
  })();

  await Promise.allSettled([pageSizeTask, sortTask]);
}

function bootDeferredTaxonomy() {
  if (!document.querySelector('.collection-taxonomy')) return;

  whenIdle(async () => {
    await import('#collection-taxonomy');
  });
}

function bootCollectionPage() {
  void bootCollectionControls().catch(() => {
    /* importmap / CDN failure — native fallback below still helps */
  });
  bootDeferredTaxonomy();
}

/**
 * Last-resort navigation if module init fails (import map / race).
 * Only fires when the enhanced handlers did not already navigate.
 */
function bindNativeControlFallback() {
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement)) return;

      const isSort = target.hasAttribute('data-collection-sort');
      const isPageSize = target.hasAttribute('data-collection-page-size');
      if (!isSort && !isPageSize) return;

      // Enhanced modules already bound — they set this flag.
      if (target.dataset.controlsBound === 'true') return;

      const url = new URL(window.location.href);
      if (isSort) {
        const value = String(target.value || '').trim();
        if (!value) url.searchParams.delete('sort_by');
        else url.searchParams.set('sort_by', value);
      }
      if (isPageSize) {
        const value = String(target.value || '').trim();
        if (!value) url.searchParams.delete('page_size');
        else url.searchParams.set('page_size', value);
      }
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    },
    true,
  );
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  bindNativeControlFallback();

  window.addEventListener('pageshow', (event) => {
    const container = document.querySelector('[data-collection-product-grid]');
    if (!container) return;

    void Promise.all([
      import('#collection-bfcache'),
      import('#collection-page-size'),
    ]).then(([{ shouldForceReloadAfterBfcache }, { shouldReloadGridForPageSize }]) => {
      if (
        shouldForceReloadAfterBfcache(
          event,
          window.location.href,
          container,
          shouldReloadGridForPageSize,
        )
      ) {
        window.location.reload();
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCollectionPage, { once: true });
  } else {
    bootCollectionPage();
  }
}
